import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthContext.jsx";
import AuthShell from "../components/AuthShell.jsx";

export default function Register() {
  const { t } = useTranslation();
  const { register, isAuthenticated, bootstrapping } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    password: "",
    password_confirm: "",
  });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  if (!bootstrapping && isAuthenticated) return <Navigate to="/dashboard" replace />;

  const onChange = (field) => (e) =>
    setForm((s) => ({ ...s, [field]: e.target.value }));

  async function onSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await register(form);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const data = err?.response?.data;
      if (typeof data === "object") setErrors(data);
      else setErrors({ _form: t("auth.registrationFailed") });
    } finally {
      setBusy(false);
    }
  }

  const fieldError = (name) =>
    Array.isArray(errors[name]) ? errors[name][0] : errors[name];

  return (
    <AuthShell
      title={t("auth.registerTitle")}
      subtitle={t("auth.registerSubtitle")}
      footer={
        <span>
          {t("auth.haveAccount")}{" "}
          <Link to="/login" className="text-brand-700 hover:underline">
            {t("common.signIn")}
          </Link>
        </span>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700">
              {t("common.firstName")}
            </label>
            <input
              className="input mt-1"
              value={form.first_name}
              onChange={onChange("first_name")}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              {t("common.lastName")}
            </label>
            <input
              className="input mt-1"
              value={form.last_name}
              onChange={onChange("last_name")}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">
            {t("common.email")}
          </label>
          <input
            type="email"
            className="input mt-1"
            value={form.email}
            onChange={onChange("email")}
            required
            autoComplete="email"
          />
          {fieldError("email") && (
            <p className="mt-1 text-sm text-red-600">{fieldError("email")}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">
            {t("common.password")}
          </label>
          <input
            type="password"
            className="input mt-1"
            value={form.password}
            onChange={onChange("password")}
            required
            minLength={8}
            autoComplete="new-password"
          />
          {fieldError("password") && (
            <p className="mt-1 text-sm text-red-600">{fieldError("password")}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">
            {t("auth.confirmPassword")}
          </label>
          <input
            type="password"
            className="input mt-1"
            value={form.password_confirm}
            onChange={onChange("password_confirm")}
            required
            minLength={8}
            autoComplete="new-password"
          />
          {fieldError("password_confirm") && (
            <p className="mt-1 text-sm text-red-600">
              {fieldError("password_confirm")}
            </p>
          )}
        </div>
        {errors._form && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
            {errors._form}
          </div>
        )}
        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? t("auth.registerBusy") : t("auth.registerButton")}
        </button>
      </form>
    </AuthShell>
  );
}
